/**
 * Job Heartbeat Manager
 *
 * Manages client heartbeats for long-running jobs to enable dead client detection.
 * The server uses heartbeats to detect when a client crashes without properly
 * disconnecting from the SSE stream.
 *
 * Usage:
 *   import { heartbeatManager, generateClientId } from '@/lib/heartbeat';
 *
 *   // When starting a job
 *   const clientId = generateClientId();
 *   heartbeatManager.start(jobId, clientId);
 *
 *   // When job completes or errors
 *   heartbeatManager.stop(jobId);
 *
 *   // When unmounting
 *   heartbeatManager.stopAll();
 */

// Default heartbeat interval (30 seconds)
const DEFAULT_HEARTBEAT_INTERVAL_MS = 30000;

// API base URL
const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Heartbeat response from the server.
 */
interface HeartbeatResponse {
  server_time: string;
  job_status: string;
  heartbeat_interval_ms: number;
  is_terminal: boolean;
  message: string;
}

/**
 * Heartbeat error callback.
 */
export type HeartbeatErrorCallback = (jobId: string, error: Error, consecutiveFailures: number) => void;

/**
 * Heartbeat success callback.
 */
export type HeartbeatSuccessCallback = (jobId: string) => void;

/**
 * Heartbeat state for a single job.
 */
interface HeartbeatState {
  jobId: string;
  clientId: string;
  intervalId: ReturnType<typeof setInterval> | null;
  lastHeartbeat: Date | null;
  isActive: boolean;
  consecutiveFailures: number;
  onError?: HeartbeatErrorCallback;
  onSuccess?: HeartbeatSuccessCallback;
}

/**
 * Options for starting heartbeats.
 */
export interface HeartbeatStartOptions {
  intervalMs?: number;
  onError?: HeartbeatErrorCallback;
  onSuccess?: HeartbeatSuccessCallback;
}

/**
 * Job Heartbeat Manager
 *
 * Manages heartbeat intervals for multiple jobs concurrently.
 * Automatically stops heartbeats when jobs enter terminal states.
 */
class JobHeartbeatManager {
  private heartbeats: Map<string, HeartbeatState> = new Map();
  private defaultInterval: number = DEFAULT_HEARTBEAT_INTERVAL_MS;

  /**
   * Start sending heartbeats for a job.
   *
   * @param jobId - Job UUID
   * @param clientId - Unique client identifier
   * @param optionsOrIntervalMs - Heartbeat options or interval in milliseconds (optional)
   */
  start(
    jobId: string,
    clientId: string,
    optionsOrIntervalMs?: number | { intervalMs?: number; onError?: HeartbeatErrorCallback; onSuccess?: HeartbeatSuccessCallback }
  ): void {
    // Stop existing heartbeat for this job if any
    if (this.heartbeats.has(jobId)) {
      this.stop(jobId);
    }

    // Handle both old signature (number) and new signature (options object)
    let interval = this.defaultInterval;
    let onError: HeartbeatErrorCallback | undefined;
    let onSuccess: HeartbeatSuccessCallback | undefined;

    if (typeof optionsOrIntervalMs === 'number') {
      interval = optionsOrIntervalMs;
    } else if (optionsOrIntervalMs) {
      interval = optionsOrIntervalMs.intervalMs || this.defaultInterval;
      onError = optionsOrIntervalMs.onError;
      onSuccess = optionsOrIntervalMs.onSuccess;
    }

    const state: HeartbeatState = {
      jobId,
      clientId,
      intervalId: null,
      lastHeartbeat: null,
      isActive: true,
      consecutiveFailures: 0,
      onError,
      onSuccess,
    };

    // IMPORTANT: Set state BEFORE calling sendHeartbeat so it can find the state
    this.heartbeats.set(jobId, state);

    // Start the heartbeat interval
    state.intervalId = setInterval(() => {
      this.sendHeartbeat(jobId, clientId);
    }, interval);

    // Send initial heartbeat immediately
    this.sendHeartbeat(jobId, clientId);
  }

  /**
   * Stop sending heartbeats for a job.
   *
   * @param jobId - Job UUID
   */
  stop(jobId: string): void {
    const state = this.heartbeats.get(jobId);
    if (!state) return;

    if (state.intervalId) {
      clearInterval(state.intervalId);
    }

    state.isActive = false;
    this.heartbeats.delete(jobId);
  }

  /**
   * Stop all heartbeats (call on unmount).
   */
  stopAll(): void {
    for (const jobId of this.heartbeats.keys()) {
      this.stop(jobId);
    }
  }

  /**
   * Check if heartbeats are active for a job.
   *
   * @param jobId - Job UUID
   * @returns True if heartbeats are active
   */
  isActive(jobId: string): boolean {
    const state = this.heartbeats.get(jobId);
    return state?.isActive ?? false;
  }

  /**
   * Get all active job IDs.
   *
   * @returns List of job IDs with active heartbeats
   */
  getActiveJobs(): string[] {
    return Array.from(this.heartbeats.keys());
  }

  /**
   * Set the default heartbeat interval.
   *
   * @param intervalMs - Interval in milliseconds
   */
  setDefaultInterval(intervalMs: number): void {
    this.defaultInterval = intervalMs;
  }

  /**
   * Get consecutive failure count for a job.
   *
   * @param jobId - Job UUID
   * @returns Number of consecutive failures, or 0 if job not found
   */
  getConsecutiveFailures(jobId: string): number {
    const state = this.heartbeats.get(jobId);
    return state?.consecutiveFailures ?? 0;
  }

  /**
   * Send a heartbeat to the server.
   *
   * @param jobId - Job UUID
   * @param clientId - Client identifier
   */
  private async sendHeartbeat(jobId: string, clientId: string): Promise<void> {
    const state = this.heartbeats.get(jobId);
    if (!state || !state.isActive) return;

    try {
      const response = await fetch(
        `${API_URL}/api/v1/jobs/${jobId}/heartbeat?client_id=${encodeURIComponent(clientId)}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        // Job not found (404) - stop heartbeats
        if (response.status === 404) {
          this.stop(jobId);
          return;
        }
        throw new Error(`Heartbeat failed: ${response.status}`);
      }

      const data: HeartbeatResponse = await response.json();
      state.lastHeartbeat = new Date();

      // Reset failure count on success
      state.consecutiveFailures = 0;

      // Call success callback
      state.onSuccess?.(jobId);

      // If job is terminal, stop sending heartbeats
      if (data.is_terminal) {
        this.stop(jobId);
        return;
      }

      // Update interval if server suggests a different one
      if (data.heartbeat_interval_ms && data.heartbeat_interval_ms !== this.defaultInterval) {
        // Restart with new interval
        const interval = state.intervalId;
        if (interval) {
          clearInterval(interval);
        }
        state.intervalId = setInterval(() => {
          this.sendHeartbeat(jobId, clientId);
        }, data.heartbeat_interval_ms);
      }
    } catch (error) {
      // Track consecutive failures
      state.consecutiveFailures++;

      // Log the failure
      console.warn(`Heartbeat failed for job ${jobId} (attempt ${state.consecutiveFailures}):`, error);

      // Call error callback with failure count
      const err = error instanceof Error ? error : new Error(String(error));
      state.onError?.(jobId, err, state.consecutiveFailures);
    }
  }
}

// Global singleton instance
export const heartbeatManager = new JobHeartbeatManager();

/**
 * Generate a unique client ID.
 *
 * Uses crypto.randomUUID() if available, otherwise falls back to
 * a timestamp-based ID.
 *
 * @returns Unique client identifier
 */
export function generateClientId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `client_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Get or create a persistent client ID stored in localStorage.
 *
 * This ensures the same client ID is used across page refreshes,
 * allowing the server to track the same client even after reconnection.
 *
 * @param storageKey - localStorage key (default: 'sse_client_id')
 * @returns Client identifier
 */
export function getOrCreateClientId(storageKey: string = 'sse_client_id'): string {
  let clientId = localStorage.getItem(storageKey);
  if (!clientId) {
    clientId = generateClientId();
    localStorage.setItem(storageKey, clientId);
  }
  return clientId;
}

export default heartbeatManager;
